import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users,
  Download,
  Eye,
  LogOut,
  Search,
  BarChart3,
  RefreshCw,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [stats, setStats] = useState({ total_tests: 0, recent_tests: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const auth = sessionStorage.getItem("adminAuth");
    if (!auth) {
      toast.error("Please login first");
      navigate("/admin");
      return;
    }
    fetchData();
  }, [navigate]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = results.filter(
        (r) =>
          r.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.user_email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredResults(filtered);
    } else {
      setFilteredResults(results);
    }
  }, [searchTerm, results]);

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem("adminAuth");
    return { Authorization: `Basic ${auth}` };
  };

  const fetchData = async () => {
    try {
      const [resultsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/results`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),
      ]);
      setResults(resultsRes.data);
      setFilteredResults(resultsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      if (error.response?.status === 401) {
        sessionStorage.removeItem("adminAuth");
        toast.error("Session expired. Please login again.");
        navigate("/admin");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    toast.success("Logged out successfully");
    navigate("/admin");
  };

  const handleViewResult = (id) => {
    window.open(`/results/${id}`, "_blank");
  };

  const handleDownloadPDF = (id, userName) => {
    // Open results page and trigger download
    window.open(`/results/${id}?download=true`, "_blank");
    toast.info(`Opening PDF download for ${userName}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getScoreColor = (score) => {
    if (score <= 30) return "text-red-600 bg-red-50";
    if (score <= 70) return "text-sky-600 bg-amber-50";
    return "text-green-600 bg-green-50";
  };

  const handleExportCSV = async () => {
    try {
      const auth = sessionStorage.getItem("adminAuth");
      const response = await axios.get(`${API}/admin/export/csv`, {
        headers: { Authorization: `Basic ${auth}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "personality_test_results.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch {
      toast.error("Failed to export CSV");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-amber-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="https://integritygo.com/" target="_blank" rel="noopener noreferrer">
                <img
                  src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/wzCO7KFXOfMKBfoO2dpF/media/6774787e91c39998b2e142f0.jpeg"
                  alt="IntegrityGO"
                  className="h-10"
                />
              </a>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-500">Personality Assessment</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleExportCSV}
                className="gap-2"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                onClick={fetchData}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Total Assessments</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_tests}</p>
                  </div>
                  <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-sky-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">This Week</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.recent_tests}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Results Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Assessment Results
                </CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredResults.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {searchTerm ? "No results match your search" : "No assessments yet"}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-center">E</TableHead>
                        <TableHead className="text-center">A</TableHead>
                        <TableHead className="text-center">C</TableHead>
                        <TableHead className="text-center">N</TableHead>
                        <TableHead className="text-center">O</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredResults.map((result) => (
                        <TableRow key={result.id} data-testid={`result-row-${result.id}`}>
                          <TableCell className="font-medium">{result.user_name}</TableCell>
                          <TableCell className="text-gray-600">{result.user_email}</TableCell>
                          <TableCell className="text-gray-500 text-sm">
                            {formatDate(result.completed_at)}
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getScoreColor(result.extraversion)}`}>
                              {Math.round(result.extraversion)}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getScoreColor(result.agreeableness)}`}>
                              {Math.round(result.agreeableness)}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getScoreColor(result.conscientiousness)}`}>
                              {Math.round(result.conscientiousness)}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getScoreColor(result.neuroticism)}`}>
                              {Math.round(result.neuroticism)}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getScoreColor(result.openness)}`}>
                              {Math.round(result.openness)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewResult(result.id)}
                                className="gap-1"
                                data-testid={`view-btn-${result.id}`}
                              >
                                <Eye className="w-4 h-4" />
                                View
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Legend */}
        <div className="mt-6 flex items-center justify-center gap-6 text-sm text-gray-500">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-red-100"></span>
            Low (0-30)
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-sky-100"></span>
            Average (31-70)
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-green-100"></span>
            High (71-100)
          </span>
          <span className="ml-4">
            E=Extraversion, A=Agreeableness, C=Conscientiousness, N=Neuroticism, O=Openness
          </span>
        </div>
      </main>
    </div>
  );
}
